import { useEffect, useState } from "react";
import { NewSideBarAdmin } from "../../NewSideBarAdmin";
import "./expedition.css";
import axios from "axios";

interface Societe {
  id: number;
  NomSociete: string;
  AdresseSociete: string;
  Prix: string;
  Status: string;
  Date: string;
}

interface Popup {
  show: boolean;
  type: "success" | "error" | "warning";
  message: string;
}

const ExpeditionAdmin = () => {
  const [societes, setSocietes] = useState<Societe[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ nom: "", adresse: "", prix: "" });
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [popup, setPopup] = useState<Popup>({ show: false, type: "success", message: "" });
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  // Fetch societes on mount
  useEffect(() => {
    fetchSocietes();
  }, []);

  // Auto-hide popup after 3s
  useEffect(() => {
    if (popup.show) {
      const timer = setTimeout(() => setPopup((p) => ({ ...p, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [popup.show]);

  const showPopup = (type: Popup["type"], message: string) => {
    setPopup({ show: true, type, message });
  };

  const fetchSocietes = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/admin/societe-livraison`
      );
      setSocietes(data);
    } catch (error) {
      showPopup("error", "Erreur lors du chargement des sociétés");
    } finally {
      setLoading(false);
    }
  };

  const filtered = societes.filter(
    (s) =>
      s.NomSociete.toLowerCase().includes(search.toLowerCase()) ||
      s.AdresseSociete.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setForm({ nom: "", adresse: "", prix: "" });
    setEditId(null);
    setShowModal(true);
  };

  const openEdit = (s: Societe) => {
    setForm({ nom: s.NomSociete, adresse: s.AdresseSociete, prix: s.Prix });
    setEditId(s.id);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.nom.trim() || !form.adresse.trim()) {
      showPopup("warning", "Veuillez remplir le nom et l'adresse de la société");
      return;
    }
    setLoading(true);

    const finalPrix = form.prix.trim() === "" ? "-" : form.prix;

    try {
      if (editId !== null) {
        await axios.post(
          `${process.env.REACT_APP_PHP_APP_URL}/admin/societe-livraison/${editId}/update`,
          {
            NomSociete: form.nom,
            AdresseSociete: form.adresse,
            Prix: finalPrix,
          }
        );
        showPopup("success", "Société modifiée avec succès ✅");
      } else {
        await axios.post(
          `${process.env.REACT_APP_PHP_APP_URL}/admin/societe-livraison/create`,
          {
            NomSociete: form.nom,
            AdresseSociete: form.adresse,
            Prix: finalPrix,
          }
        );
        showPopup("success", "Société ajoutée avec succès ✅");
      }
      await fetchSocietes();
      setShowModal(false);
      setForm({ nom: "", adresse: "", prix: "" });
      setEditId(null);
    } catch (error) {
      showPopup("error", "Erreur lors de l'opération ❌");
    }
    setLoading(false);
  };

  const handleDelete = async (id: number) => {
    setDeleteLoading(id);
    try {
      await axios.delete(
        `${process.env.REACT_APP_PHP_APP_URL}/admin/societe-livraison/${id}/delete`
      );
      await fetchSocietes();
      showPopup("success", "Société supprimée avec succès ✅");
    } catch (error) {
      showPopup("error", "Erreur lors de la suppression ❌");
    }
    setDeleteLoading(null);
    setConfirmDelete(null);
  };

  return (
    <>
      <NewSideBarAdmin />
      <div className="exp-root">
        {/* ── Popup Toast ── */}
        <div className={`exp-toast ${popup.show ? "exp-toast--visible" : ""} exp-toast--${popup.type}`}>
          <div className="exp-toast-icon">
            <i className={`bi ${
              popup.type === "success" ? "bi-check-circle-fill" :
              popup.type === "error" ? "bi-x-circle-fill" :
              "bi-exclamation-triangle-fill"
            }`} />
          </div>
          <span className="exp-toast-msg">{popup.message}</span>
          <button className="exp-toast-close" onClick={() => setPopup((p) => ({ ...p, show: false }))}>
            <i className="bi bi-x" />
          </button>
        </div>

        {/* ── Confirm Delete Modal ── */}
        {confirmDelete !== null && (
          <>
            <div className="exp-overlay" onClick={() => setConfirmDelete(null)} />
            <div className="exp-modal exp-modal--confirm">
              <div className="exp-confirm-body">
                <div className="exp-confirm-icon">
                  <i className="bi bi-exclamation-triangle-fill" />
                </div>
                <h3>Supprimer cette société ?</h3>
                <p>Cette action est irréversible. La société sera définitivement supprimée.</p>
                <div className="exp-confirm-actions">
                  <button className="exp-btn-cancel" onClick={() => setConfirmDelete(null)}>
                    Annuler
                  </button>
                  <button
                    className="exp-btn-delete"
                    onClick={() => handleDelete(confirmDelete)}
                    disabled={deleteLoading === confirmDelete}
                  >
                    {deleteLoading === confirmDelete ? (
                      <>
                        <span className="exp-spinner" />
                        Suppression...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-trash3" />
                        Supprimer
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Header */}
        <div className="exp-header">
          <div>
            <h1 className="exp-title">Expédition</h1>
            <p className="exp-subtitle">
              Gérez vos sociétés de livraison et tarifs d'expédition
            </p>
          </div>
          <button className="exp-add-btn" onClick={openAdd}>
            <i className="bi bi-plus-lg" />
            <span>Société de livraison</span>
          </button>
        </div>

        {/* Stats */}
        <div className="exp-stats">
          <div className="exp-stat-card">
            <div className="exp-stat-icon">
              <i className="bi bi-truck" />
            </div>
            <div>
              <span className="exp-stat-value">{societes.length}</span>
              <span className="exp-stat-label">Transporteurs</span>
            </div>
          </div>
          <div className="exp-stat-card">
            <div className="exp-stat-icon exp-stat-icon--blue">
              <i className="bi bi-geo-alt" />
            </div>
            <div>
              <span className="exp-stat-value">
                {new Set(societes.map((s) => s.AdresseSociete.split(",")[0]?.trim())).size}
              </span>
              <span className="exp-stat-label">Villes</span>
            </div>
          </div>
          <div className="exp-stat-card">
            <div className="exp-stat-icon exp-stat-icon--green">
              <i className="bi bi-currency-exchange" />
            </div>
            <div>
              <span className="exp-stat-value">
                {societes.length > 0
                  ? (
                      societes.reduce((a, s) => a + (s.Prix === "-" ? 0 : parseFloat(s.Prix || "0")), 0) /
                      societes.filter((s) => s.Prix !== "-").length || 1
                    ).toFixed(0)
                  : 0}{" "}
                <small>MAD</small>
              </span>
              <span className="exp-stat-label">Prix moyen</span>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="exp-toolbar">
          <div className="exp-search">
            <i className="bi bi-search" />
            <input
              type="text"
              placeholder="Rechercher une société..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="exp-search-clear" onClick={() => setSearch("")}>
                <i className="bi bi-x" />
              </button>
            )}
          </div>
          <span className="exp-count">
            <b>{filtered.length}</b> résultat{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Table */}
        <div className="exp-table-wrap">
          <table className="exp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Société</th>
                <th>Adresse</th>
                <th>Prix (MAD)</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="exp-empty">
                      <span className="exp-spinner" style={{ width: "32px", height: "32px", borderWidth: "3px", marginBottom: "16px" }} />
                      <p>Chargement des sociétés...</p>
                    </div>
                  </td>
                </tr>
              ) : filtered.length > 0 ? (
                filtered.map((s, i) => (
                  <tr key={s.id}>
                    <td className="exp-td-num">{i + 1}</td>
                    <td>
                      <div className="exp-societe-cell">
                        <div className="exp-societe-avatar">
                          {s.NomSociete.charAt(0).toUpperCase()}
                        </div>
                        <span className="exp-societe-name">{s.NomSociete}</span>
                      </div>
                    </td>
                    <td>
                      <span className="exp-address">
                        <i className="bi bi-geo-alt" /> {s.AdresseSociete}
                      </span>
                    </td>
                    <td>
                      <span className="exp-prix">
                        {s.Prix === "-" ? "Non défini" : `${parseFloat(s.Prix).toFixed(2)} MAD`}
                      </span>
                    </td>
                    <td>
                      <span className={`exp-status ${s.Status === 'active' ? 'exp-status--active' : 'exp-status--inactive'}`}>
                        {s.Status}
                      </span>
                    </td>
                    <td>
                      <span className="exp-date">
                        {s.Date ? new Date(s.Date).toLocaleDateString('fr-FR') : '-'}
                      </span>
                    </td>
                    <td>
                      <div className="exp-actions">
                        <button
                          className="exp-action-btn exp-action-edit"
                          onClick={() => openEdit(s)}
                          title="Modifier"
                        >
                          <i className="bi bi-pencil-square" />
                        </button>
                        <button
                          className="exp-action-btn exp-action-delete"
                          onClick={() => setConfirmDelete(s.id)}
                          title="Supprimer"
                        >
                          <i className="bi bi-trash3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <div className="exp-empty">
                      <i className="bi bi-inbox" />
                      <p>Aucune société trouvée</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <>
            <div className="exp-overlay" onClick={() => !loading && setShowModal(false)} />
            <div className="exp-modal">
              {/* Loader overlay inside modal */}
              {loading && (
                <div className="exp-modal-loader">
                  <span className="exp-spinner exp-spinner--lg" />
                  <p>{editId ? "Modification en cours..." : "Ajout en cours..."}</p>
                </div>
              )}
              <div className="exp-modal-header">
                <h3>{editId ? "Modifier la société" : "Nouvelle société de livraison"}</h3>
                <button className="exp-modal-close" onClick={() => !loading && setShowModal(false)}>
                  <i className="bi bi-x-lg" />
                </button>
              </div>
              <div className="exp-modal-body">
                <div className="exp-field">
                  <label>
                    <i className="bi bi-building" /> Nom de la société
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Amana Express"
                    value={form.nom}
                    onChange={(e) => setForm({ ...form, nom: e.target.value })}
                    disabled={loading}
                  />
                </div>
                <div className="exp-field">
                  <label>
                    <i className="bi bi-geo-alt" /> Adresse
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Casablanca, Maroc"
                    value={form.adresse}
                    onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                    disabled={loading}
                  />
                </div>
                <div className="exp-field">
                  <label>
                    <i className="bi bi-tag" /> Prix (MAD) - Optionnel
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: 45.00 ou laissez vide"
                    value={form.prix}
                    onChange={(e) => setForm({ ...form, prix: e.target.value })}
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="exp-modal-footer">
                <button className="exp-btn-cancel" onClick={() => !loading && setShowModal(false)} disabled={loading}>
                  Annuler
                </button>
                <button className="exp-btn-submit" onClick={handleSubmit} disabled={loading}>
                  {loading ? (
                    <>
                      <span className="exp-spinner" />
                      En cours...
                    </>
                  ) : (
                    <>
                      <i className={`bi ${editId ? "bi-check-lg" : "bi-plus-lg"}`} />
                      {editId ? "Modifier" : "Ajouter"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ExpeditionAdmin;
